<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import { format as formatDate } from "date-fns";
import { Loader2, RotateCcw } from "@lucide/vue";

import { useModalStore } from "@/stores/modals/modals.store";
import TextInputNew from "@/components/Reusables/Inputs/TextInputNew.vue";
import { getVersionSpeckleConnection, restoreProjectVersion } from "@/api/services/ProjectRevision";
import SpeckleStreamChoice from "./components/SpeckleStreamChoice.vue";
import {
  NEW_STREAM_SELECTION,
  offersSpeckleStreamChoice,
  RESTORE_PREFERRED_MODE,
  type SpeckleStreamSelection,
} from "./components/speckleStreamChoice";
import { PROJECT_NAME_TAKEN_MESSAGE, useProjectNameAvailability } from "@/composables/useProjectNameAvailability.composable";
import type { ProjectVersion } from "../types/projectVersion.types";

// Restore-into-new-project confirmation. The backend replays the source project's
// event stream up to this version's pin into a brand-new project — materials and project
// information exactly as they were — and copies the source trail up to (and including) this
// version, so the new project's History still tells the story that led to the restored state.
// The source project is never touched. Admins only.
const props = defineProps<{
  projectId: string;
  version: ProjectVersion;
  projectName: string;
  /**
   * The SOURCE project's Speckle project id — `speckleStreamId` in Hasura, which kept the old word
   * for what Speckle now calls a project. Decides whether the Speckle choice is offered at all: a
   * project with no Speckle project has nothing to share and nothing to replace, so the API ignores
   * the choice either way (RTLCA-5456).
   */
  speckleStreamId?: string;
}>();

const router = useRouter();
const toast = useToast();
const { closeByName } = useModalStore();

const newProjectName = ref(`${props.projectName} (v${props.version.versionNumber})`);
const isRestoring = ref(false);

/**
 * The same tenant-wide duplicate-name check the create/edit wizard runs (RTLCA-5603). No exempt name:
 * `X (v3)` is a name the system guessed rather than one this dialog owns, and restoring the same
 * version twice proposes it again — which the backend accepts silently.
 */
const { nameExists: nameTaken, check: checkName, checkImmediately: checkNameNow } = useProjectNameAvailability();

const nameError = computed(() => {
  if (!newProjectName.value.trim()) return "Give the new project a name";
  return nameTaken.value ? PROJECT_NAME_TAKEN_MESSAGE : "";
});

// The seeded name is the one most likely to collide, and a user who accepts it without touching the
// field would never trigger an input-driven check.
onMounted(() => checkNameNow(newProjectName.value.trim()));

// The Speckle choice never blocks the confirm: both options are always answerable — one creates a
// project, the other keeps the one this project already has — so nameError stays the only guard.
//
// Seeded with NEW_STREAM_SELECTION rather than the restore preference: this ref is what gets SENT,
// and it is only overwritten once the block mounts and emits. When the source project has no Speckle
// project the block never renders, and `ExistingStream` has no id to resolve to — so the value held
// while nothing is offered has to be the new-project one.
const streamSelection = ref<SpeckleStreamSelection>(NEW_STREAM_SELECTION);

// The Speckle project the restore would actually use is the version's, not the live one; null means none.
const versionSpeckleStreamId = ref<string | null>(null);
const isResolvingConnection = ref(true);

onMounted(async () => {
  try {
    versionSpeckleStreamId.value = await getVersionSpeckleConnection(props.projectId, props.version.id);
  } catch (e) {
    Sentry.captureException(e);
    versionSpeckleStreamId.value = props.speckleStreamId ?? null;
  } finally {
    isResolvingConnection.value = false;
  }
});

const offersStreamChoice = computed(() => offersSpeckleStreamChoice(versionSpeckleStreamId.value));
const hasNoVersionConnection = computed(() => !isResolvingConnection.value && !offersStreamChoice.value);
const confirmBlocked = computed(() => !!nameError.value || isRestoring.value || isResolvingConnection.value);

const versionDate = computed(() => formatDate(new Date(props.version.createdAt), "MMM d, yyyy 'at' HH:mm"));

const cancel = () => closeByName("RestoreProjectVersionModal", { action: "cancel" });

// The API rejects a Speckle choice it cannot honour with a 400 whose body carries the reason —
// reachable from here only on "Create new project", which needs a linked Speckle account to create
// one with. Worth reading; anything else gets the generic line, the way the create wizard does it.
const restoreErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message: unknown = error.response?.data?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return "Could not restore project. Please try again.";
};

const restore = async () => {
  // nameError now covers the duplicate name as well as the empty one, so this guard closes the gap
  // between a check landing and the button re-rendering — a fast click cannot beat it.
  if (confirmBlocked.value) return;
  isRestoring.value = true;
  const name = newProjectName.value.trim();
  const { mode, speckleStreamId } = streamSelection.value;
  try {
    const newProjectId = await restoreProjectVersion(props.projectId, props.version.id, name, mode, speckleStreamId);
    toast.success(`Created “${name}” from version ${props.version.versionNumber}`);
    closeByName("RestoreProjectVersionModal", { action: "restored", newProjectId });
    void router.push(`/project/${newProjectId}`);
  } catch (e) {
    Sentry.captureException(e);
    toast.error(restoreErrorMessage(e));
  } finally {
    isRestoring.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Restore version {{ version.versionNumber }}</h2>

    <p class="t-small text-type-muted">
      This creates a <span class="t-small-bold text-type-default">new project</span> with the materials and project information as
      they were at version {{ version.versionNumber }}, along with the transport and waste mappings, custom emission factors, and
      the history up to that point. The current project is not changed.
    </p>

    <!-- What you're restoring, on the history page's own grammar: the vN pill in the left rail, the
         summary and its meta line beside it, so the card reads as the row that was clicked. The
         history page's pill is bg-slate-100 against a white row; here the card IS slate-100, so the
         pill takes bg-slate-0 to keep the same figure-on-ground. Rendered unconditionally because
         Restore is offered only on numbered rows (`canRestore && isVersion` in VersionRow) — an
         informational row has no number and cannot reach this dialog. -->
    <div class="rounded-8 p-px-12 gap-px-12 flex items-start bg-slate-100 ring-1 ring-slate-200">
      <span
        class="t-caption-bold text-type-muted bg-slate-0 mt-px-2 w-px-40 shrink-0 rounded-full px-2 py-0.5 text-center tabular-nums ring-1 ring-slate-200"
        :title="`Version ${version.versionNumber}`"
        data-cy="restore-version-number">
        v{{ version.versionNumber }}
      </span>
      <div class="gap-px-4 flex min-w-0 flex-1 flex-col">
        <p class="t-small-bold text-type-default">
          {{ version.summary }}<span v-if="version.entityLabel"> — {{ version.entityLabel }}</span>
        </p>
        <p class="t-caption text-type-muted">{{ versionDate }} · {{ version.createdBy.name }}</p>
      </div>
    </div>

    <!-- Trimmed on the way into the check because trimmed is what `restore()` submits, and the API
         compares the name with `_eq` — no trim, no case folding — so the two have to agree. -->
    <TextInputNew
      v-model="newProjectName"
      label="New project name"
      :error-msg="nameError"
      :disabled="isRestoring"
      required
      @update:model-value="(value: string) => checkName(value.trim())" />

    <!-- Which Speckle project the restored copy connects to. Only offered when the source project
         has one — see offersSpeckleStreamChoice; the second test narrows the id to a string.
         Restoring leads with "Use the same project": it brings back a state that WAS this project, so
         staying on the models the project is already fed by is the expected answer here. -->
    <SpeckleStreamChoice
      v-if="offersStreamChoice && versionSpeckleStreamId"
      :source-speckle-stream-id="versionSpeckleStreamId"
      :preferred-mode="RESTORE_PREFERRED_MODE"
      :disabled="isRestoring"
      @update:selection="streamSelection = $event" />
    <p
      v-else-if="hasNoVersionConnection && speckleStreamId"
      class="t-small text-type-muted"
      data-cy="restore-no-speckle-note">
      This version is from before the Speckle integration was added, so the new project will have none.
    </p>

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isRestoring"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isRestoring },
        ]"
        data-cy="restore-cancel"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="confirmBlocked"
        :class="[
          btnBase,
          !confirmBlocked
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': confirmBlocked },
        ]"
        data-cy="restore-confirm"
        @click="restore">
        <Loader2
          v-if="isRestoring"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        <RotateCcw
          v-else
          class="icon-sm"
          :stroke-width="1.5" />
        {{ isRestoring ? "Restoring…" : "Restore as new project" }}
      </button>
    </div>
  </div>
</template>
